import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { computeMaintStatus, STATUS_COLORS, fmtNum, fmtDate } from "../lib/status";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function Maintenance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [fleet, setFleet] = useState([]);
  const [intervals, setIntervals] = useState([]);
  const [tab, setTab] = useState("all");
  const [editingRule, setEditingRule] = useState(null);

  const load = async () => {
    const [f, i] = await Promise.all([api.get("/fleet"), api.get("/intervals")]);
    setFleet(f.data); setIntervals(i.data);
  };
  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, []);

  const enriched = useMemo(() => fleet.map((u) => ({ u, s: computeMaintStatus(u, intervals) })), [fleet, intervals]);
  const groups = { overdue: [], due_soon: [], needs_data: [], ok: [] };
  enriched.forEach((e) => { if (groups[e.s.key]) groups[e.s.key].push(e); });

  const filtered = tab === "all" ? enriched : enriched.filter((e) => e.s.key === tab);

  const tabs = [
    { k: "all", label: "All", count: enriched.length },
    { k: "overdue", label: "Overdue", count: groups.overdue.length },
    { k: "due_soon", label: "Due Soon", count: groups.due_soon.length },
    { k: "needs_data", label: "Needs Data", count: groups.needs_data.length },
    { k: "ok", label: "OK", count: groups.ok.length },
  ];

  return (
    <div className="space-y-4" data-testid="maintenance-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Maintenance</h1>
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-1">Service status across the fleet</p>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`maint-tab-${t.k}`}
            className={`shrink-0 text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors flex items-center gap-2 ${
              tab === t.k ? "bg-zinc-100 text-zinc-900 border-zinc-100" : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800"
            }`}>
            {t.label} <span className="opacity-70">{t.count}</span>
          </button>
        ))}
      </div>

      {/* Intervals table */}
      <div className="border border-zinc-800 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold">Service Rules</h3>
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
              Sets when each vehicle type is due for service
            </p>
          </div>
          {isAdmin && (
            <button onClick={() => setEditingRule("new")} data-testid="add-rule-btn"
              className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-sm transition-colors flex items-center gap-1.5 shrink-0">
              <Plus className="h-3 w-3" /> Add Rule
            </button>
          )}
        </div>
        <table className="w-full text-xs font-mono">
          <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-right px-4 py-2">Miles</th>
              <th className="text-right px-4 py-2">Hours</th>
              <th className="text-left px-4 py-2 hidden sm:table-cell">Basis</th>
              {isAdmin && <th className="text-right px-4 py-2 w-24">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {intervals.map((i) => (
              <tr key={i.id} data-testid={`rule-row-${i.id}`}>
                <td className="px-4 py-2 text-zinc-200">{i.type}</td>
                <td className="px-4 py-2 text-right text-zinc-300">{fmtNum(i.miles)}</td>
                <td className="px-4 py-2 text-right text-zinc-300">{fmtNum(i.hours)}</td>
                <td className="px-4 py-2 text-zinc-400 hidden sm:table-cell">{i.notes}</td>
                {isAdmin && (
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => setEditingRule(i)} data-testid={`rule-edit-${i.id}`}
                      className="text-zinc-400 hover:text-zinc-100 p-1 transition-colors" title="Edit"><Pencil className="h-3 w-3" /></button>
                    <button onClick={async () => {
                      if (!window.confirm(`Delete the "${i.type}" service rule? Units of that type will no longer show status badges.`)) return;
                      try { await api.delete(`/intervals/${i.id}`); toast.success("Rule deleted"); load(); }
                      catch (e) { toast.error(formatApiError(e)); }
                    }} data-testid={`rule-delete-${i.id}`}
                      className="text-zinc-400 hover:text-red-400 p-1 ml-1 transition-colors" title="Delete"><Trash2 className="h-3 w-3" /></button>
                  </td>
                )}
              </tr>
            ))}
            {intervals.length === 0 && (
              <tr><td colSpan={isAdmin ? 5 : 4} className="p-6 text-center text-zinc-500">No rules yet. Add one to start tracking service status.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editingRule && (
        <IntervalDialog rule={editingRule === "new" ? null : editingRule}
          onClose={() => setEditingRule(null)} onSaved={() => { setEditingRule(null); load(); }} />
      )}

      <div className="border border-zinc-800 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left px-4 py-2">Unit</th>
              <th className="text-left px-4 py-2 hidden sm:table-cell">Type</th>
              <th className="text-right px-4 py-2 font-mono">Mileage</th>
              <th className="text-right px-4 py-2 font-mono hidden sm:table-cell">Last Service</th>
              <th className="text-right px-4 py-2 font-mono hidden md:table-cell">Next Annual</th>
              <th className="text-right px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {filtered.map(({ u, s }) => {
              const c = STATUS_COLORS[s.key];
              return (
                <tr key={u.id} className="hover:bg-zinc-900/60 transition-colors" data-testid={`maint-row-${u.id}`}>
                  <td className="px-4 py-2">
                    <Link to="/fleet" className="font-mono font-semibold text-zinc-100 hover:text-red-400">{u.unit}</Link>
                    <div className="text-xs text-zinc-500 truncate max-w-[180px]">{u.desc}</div>
                  </td>
                  <td className="px-4 py-2 text-zinc-400 hidden sm:table-cell">{u.type}</td>
                  <td className="px-4 py-2 text-right font-mono text-zinc-200">{fmtNum(u.mileage)}</td>
                  <td className="px-4 py-2 text-right font-mono text-zinc-400 hidden sm:table-cell">{fmtDate(u.lastServiceDate) || fmtNum(u.lastServiceMileage)}</td>
                  <td className="px-4 py-2 text-right font-mono text-zinc-400 hidden md:table-cell">{fmtDate(u.nextAnnualInspection)}</td>
                  <td className="px-4 py-2 text-right">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${c.badge}`}>{s.label}</span>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-sm text-zinc-500">No units match this filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IntervalDialog({ rule, onClose, onSaved }) {
  const [form, setForm] = useState({
    type: rule?.type || "",
    miles: rule?.miles ?? "",
    hours: rule?.hours ?? "",
    notes: rule?.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.type.trim()) { toast.error("Type is required"); return; }
    setBusy(true);
    try {
      const payload = {
        type: form.type.trim(),
        miles: form.miles === "" ? null : Number(form.miles),
        hours: form.hours === "" ? null : Number(form.hours),
        notes: form.notes || null,
      };
      if (rule) await api.patch(`/intervals/${rule.id}`, payload);
      else await api.post("/intervals", payload);
      toast.success(rule ? "Rule updated" : "Rule created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader><DialogTitle>{rule ? `Edit Rule: ${rule.type}` : "New Service Rule"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Vehicle Type *</label>
            <input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="rule-type"
              placeholder="Salt Spreader, Hot Patcher, Snow Plow…" disabled={!!rule}
              className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded-sm px-2 py-1.5 font-mono text-sm outline-none disabled:opacity-60" />
            {rule && <p className="text-[10px] text-zinc-500 mt-1">Type cannot be changed. Delete and recreate if needed.</p>}
            {!rule && <p className="text-[10px] text-zinc-500 mt-1">Must exactly match the Type field on your Fleet units (case-insensitive).</p>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Miles Interval</label>
              <input type="number" value={form.miles} onChange={(e) => setForm({ ...form, miles: e.target.value })} data-testid="rule-miles"
                placeholder="e.g. 5000"
                className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded-sm px-2 py-1.5 font-mono text-sm outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Hours Interval</label>
              <input type="number" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="rule-hours"
                placeholder="e.g. 250"
                className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded-sm px-2 py-1.5 font-mono text-sm outline-none" />
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Fill in ONE of the two. Miles for road vehicles, Hours for equipment/generators. Leave both blank for annual-inspection-only (like Trailers).
          </p>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Notes</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="rule-notes"
              placeholder="Adjust per OEM manual…"
              className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded-sm px-2 py-1.5 font-mono text-sm outline-none" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="text-xs font-mono uppercase px-3 py-1.5 border border-zinc-700 rounded-sm hover:bg-zinc-800 transition-colors">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="rule-submit"
            className="text-xs font-mono uppercase px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-sm transition-colors disabled:opacity-50">
            {busy ? "Saving…" : (rule ? "Save" : "Add Rule")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
